import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { useWorkspace, workspaceParams } from "../utils/workspace";
import { WorkspaceBadge } from "../components/Workspace";
import { GakEak } from "../utils/caseFormat";
import { Clock, AlertTriangle, CheckCircle, Calendar, ChevronRight, ChevronDown, FileText, Gavel, Scale, ClipboardCheck, Loader2, AlertCircle, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";

// Εξάγει τον τίτλο του πινακίου και τη δικάσιμο από το last_hearing_date string.
// Format: "<pinakio> - <tmima> - <DD/MM/YYYY> - <eak>"
const extractPinakioInfo = (c) => {
  const lhd = c?.last_hearing_date ? String(c.last_hearing_date) : "";
  let pinakio = "—";
  let hearing = c?.hearing_date ? String(c.hearing_date) : "";
  if (lhd && !["none", "nan", "null"].includes(lhd.toLowerCase())) {
    const parts = lhd.split(" - ").map(s => s.trim());
    const code = parts[0] || "";
    const tmima = parts[1] && !/^\d{2}\/\d{2}\/\d{4}$/.test(parts[1]) ? parts[1] : "";
    pinakio = tmima ? `${code} - ${tmima}` : code;
    for (const t of parts) {
      const m = t.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (m) { hearing = `${m[3]}-${m[2]}-${m[1]}`; break; }
    }
  }
  return { pinakio, hearing };
};

// Ομαδοποιεί cases ανά (Δικάσιμος + Πινάκιο) και επιστρέφει ταξινομημένη λίστα ομάδων.
const groupCasesByPinakio = (cases) => {
  const map = {};
  (cases || []).forEach(c => {
    const { pinakio, hearing } = extractPinakioInfo(c);
    const key = `${hearing || "—"}__${pinakio}`;
    if (!map[key]) map[key] = { key, pinakio, hearing, cases: [] };
    map[key].cases.push(c);
  });
  return Object.values(map).sort((a, b) => {
    const ah = a.hearing || "9999";
    const bh = b.hearing || "9999";
    if (ah !== bh) return ah.localeCompare(bh);
    return (a.pinakio || "").localeCompare(b.pinakio || "");
  });
};

const fmtGreek = (iso) => {
  if (!iso || iso === "—") return "—";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const typeLabels = {
  service: "Επίδοση Δικογράφου",
  proposals: "Κατάθεση Προτάσεων",
  addition: "Κατάθεση Προσθήκης-Αντίκρουσης",
  judge_assignment: "Στάδιο 1 — Ορισμός Δικαστή",
  preliminary_review: "Στάδιο 2 — Προδικαστικός Έλεγχος",
  article_269_stage: "Στάδιο Άρθρου 269 (Αρχή Συγκέντρωσης)",
  late_claims: "Οψιγενείς Ισχυρισμοί",
  late_counter: "Αντίκρουση Οψιγενών",
  filing_269: "Κατάθεση Άρθρου 269 ΚΠολΔ",
  counter_269: "Αντίκρουση Άρθρου 269 ΚΠολΔ",
  counter_269_post_hearing: "Αντίκρουση Άρθρου 269 (μετά δικάσιμο)",
  order_compliance: "Συμμόρφωση Διάταξης"
};

const typeIcons = {
  service: FileText,
  proposals: ClipboardCheck,
  addition: Scale,
  judge_assignment: Gavel,
  preliminary_review: CheckCircle,
  article_269_stage: Scale,
  late_claims: AlertCircle,
  late_counter: Shield,
  filing_269: FileText,
  counter_269: Scale,
  counter_269_post_hearing: Scale,
  order_compliance: Gavel
};

const typeColors = {
  service: "bg-blue-100 text-blue-700 border-blue-200",
  proposals: "bg-purple-100 text-purple-700 border-purple-200",
  addition: "bg-teal-100 text-teal-700 border-teal-200",
  judge_assignment: "bg-amber-100 text-amber-700 border-amber-200",
  preliminary_review: "bg-emerald-100 text-emerald-700 border-emerald-200",
  article_269_stage: "bg-violet-100 text-violet-700 border-violet-200",
  late_claims: "bg-rose-100 text-rose-700 border-rose-200",
  late_counter: "bg-orange-100 text-orange-700 border-orange-200",
  filing_269: "bg-indigo-100 text-indigo-700 border-indigo-200",
  counter_269: "bg-cyan-100 text-cyan-700 border-cyan-200",
  counter_269_post_hearing: "bg-sky-100 text-sky-700 border-sky-200",
  order_compliance: "bg-yellow-100 text-yellow-700 border-yellow-200"
};

const allTypes = ["service", "proposals", "addition", "judge_assignment", "preliminary_review", "article_269_stage", "late_claims", "late_counter", "filing_269", "counter_269", "counter_269_post_hearing", "order_compliance"];

const Today = () => {
  const { user } = useAuth();
  const { ws } = useWorkspace(user);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDates, setExpandedDates] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});
  const [showAll, setShowAll] = useState(false);
  // iter109 (user): αρχική φόρτωση ΜΟΝΟ «Κατάθεση Προτάσεων» / 7 ημέρες — ο server στέλνει μόνο αυτόν τον τύπο
  const [typeFilter, setTypeFilter] = useState("proposals");
  const [daysBack, setDaysBack] = useState(7);
  const [daysAhead, setDaysAhead] = useState(7);
  // Iter77: per-(date,type) fully-loaded cases (πάνω από τα 50 του slice)
  const [loadedFull, setLoadedFull] = useState({}); // key `${date}__${type}` → cases[]
  const [loadingFull, setLoadingFull] = useState({});
  const wsQuery = () => Object.entries(workspaceParams(ws)).map(([k, v]) => `&${k}=${encodeURIComponent(v)}`).join("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDeadlines(); }, [daysBack, daysAhead, typeFilter, ws?.branch, ws?.court_type]);

  const fetchDeadlines = async () => {
    try {
      setLoading(true);
      const typesQ = typeFilter !== "all" ? `&types=${encodeURIComponent(typeFilter)}` : "";
      const res = await axios.get(`${API}/today/grouped-deadlines?days_back=${daysBack}&days_ahead=${daysAhead}${typesQ}${wsQuery()}`);
      setDates(res.data || []);
    } catch { toast.error("Σφάλμα φόρτωσης"); }
    finally { setLoading(false); }
  };

  // Iter77: Φόρτωσε ΟΛΕΣ τις υποθέσεις για συγκεκριμένη (date + type) — παρακάμπτει το backend $slice: 50.
  const fetchFullByDateAndType = async (date, type) => {
    const key = `${date}__${type}`;
    if (loadedFull[key] || loadingFull[key]) return;
    setLoadingFull(prev => ({ ...prev, [key]: true }));
    try {
      const all = [];
      const LIMIT = 500;
      let skip = 0;
      // Loop σε chunks μέχρι να τα φορτώσει όλα (max 10 iterations = 5000 cases safety)
      for (let i = 0; i < 10; i++) {
        const res = await axios.get(
          `${API}/today/deadlines/by-date-and-type?date=${encodeURIComponent(date)}&type=${encodeURIComponent(type)}&skip=${skip}&limit=${LIMIT}${wsQuery()}`
        );
        const items = res.data?.items || [];
        all.push(...items);
        if (items.length < LIMIT) break;
        skip += LIMIT;
      }
      setLoadedFull(prev => ({ ...prev, [key]: all }));
    } catch {
      toast.error("Σφάλμα φόρτωσης πλήρους λίστας");
    } finally {
      setLoadingFull(prev => ({ ...prev, [key]: false }));
    }
  };

  const filteredDates = typeFilter === "all"
    ? dates
    : dates.map(d => ({
        ...d,
        types: d.types.filter(t => t.type === typeFilter),
        total: d.types.filter(t => t.type === typeFilter).reduce((s, t) => s + t.count, 0)
      })).filter(d => d.types.length > 0);

  const overdueDates = filteredDates.filter(d => d.is_overdue);
  const todayDates = filteredDates.filter(d => d.is_today);
  const upcomingDates = filteredDates.filter(d => !d.is_overdue && !d.is_today);

  const overdueTotal = overdueDates.reduce((s, d) => s + d.total, 0);
  const todayTotal = todayDates.reduce((s, d) => s + d.total, 0);
  const upcomingTotal = upcomingDates.reduce((s, d) => s + d.total, 0);

  // Show minimum 5 upcoming dates or all
  const visibleDates = showAll
    ? filteredDates
    : [...overdueDates, ...todayDates, ...upcomingDates.slice(0, Math.max(5, 5 - overdueDates.length - todayDates.length))];

  const formatDateFull = (dateStr) => {
    try {
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString("el-GR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    } catch { return dateStr; }
  };

  const toggleDate = (date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const todayStr = new Date().toLocaleDateString("el-GR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="today-title">Σήμερα</h1>
          <p className="text-slate-500 mt-1 capitalize">{todayStr}</p>
        </div>
        {ws && <WorkspaceBadge ws={ws} />}
      </div>

      {/* Type Filter */}
      <div className="flex flex-wrap gap-2" data-testid="type-filters">
        <Button
          variant={typeFilter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setTypeFilter("all")}
          data-testid="filter-all"
        >
          Όλα
        </Button>
        {allTypes.map(type => {
          const Icon = typeIcons[type];
          return (
            <Button
              key={type}
              variant={typeFilter === type ? "default" : "outline"}
              size="sm"
              className={typeFilter === type ? "" : "text-slate-600"}
              onClick={() => setTypeFilter(type)}
              data-testid={`filter-${type}`}
            >
              <Icon className="w-3.5 h-3.5 mr-1.5" />
              {typeLabels[type]}
            </Button>
          );
        })}
      </div>

      {/* Range Controls */}
      <div className="flex items-center gap-2 text-sm" data-testid="range-controls">
        <span className="text-slate-500">Εύρος:</span>
        {[7, 30, 90, 180].map(d => (
          <Button key={d} variant={daysBack === d ? "default" : "outline"} size="sm"
            onClick={() => { setDaysBack(d); setDaysAhead(d); setShowAll(false); }}
            data-testid={`range-${d}`}>
            {d} ημ.
          </Button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={`${overdueTotal > 0 ? "border-red-300 bg-red-50/50" : ""}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${overdueTotal > 0 ? "bg-red-100" : "bg-slate-100"}`}>
              <AlertTriangle className={`w-5 h-5 ${overdueTotal > 0 ? "text-red-600" : "text-slate-400"}`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="overdue-count">{overdueTotal}</p>
              <p className="text-sm text-slate-500">Εκπρόθεσμες ({overdueDates.length} ημ.)</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${todayTotal > 0 ? "border-amber-300 bg-amber-50/50" : ""}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${todayTotal > 0 ? "bg-amber-100" : "bg-slate-100"}`}>
              <Clock className={`w-5 h-5 ${todayTotal > 0 ? "text-amber-600" : "text-slate-400"}`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="today-count">{todayTotal}</p>
              <p className="text-sm text-slate-500">Σημερινές</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="upcoming-count">{upcomingTotal}</p>
              <p className="text-sm text-slate-500">Επόμενες ({upcomingDates.length} ημερομηνίες)</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : filteredDates.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
            <h3 className="text-lg font-medium text-slate-900">Δεν υπάρχουν προθεσμίες</h3>
            <p className="text-slate-500 mt-1">Δεν υπάρχουν εκκρεμείς προθεσμίες</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {visibleDates.map(dateItem => {
            const isExpanded = expandedDates[dateItem.date] !== false;
            const borderColor = dateItem.is_overdue ? "border-red-300" : dateItem.is_today ? "border-amber-300" : "border-slate-200";
            const headerBg = dateItem.is_overdue ? "bg-red-50" : dateItem.is_today ? "bg-amber-50" : "bg-slate-50";
            const dotColor = dateItem.is_overdue ? "bg-red-500" : dateItem.is_today ? "bg-amber-500" : "bg-blue-400";

            return (
              <Card key={dateItem.date} className={borderColor} data-testid={`date-card-${dateItem.date}`}>
                <div
                  className={`flex items-center justify-between p-4 cursor-pointer rounded-t-lg ${headerBg}`}
                  onClick={() => toggleDate(dateItem.date)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${dotColor}`} />
                    <div>
                      <p className="font-semibold text-slate-900 capitalize">
                        {dateItem.is_today ? "Σήμερα" : formatDateFull(dateItem.date)}
                      </p>
                      <span className="text-xs text-slate-500">
                        {dateItem.is_overdue ? "Εκπρόθεσμη" :
                         dateItem.is_today ? "Σήμερα" :
                         `σε ${dateItem.days_remaining} ημέρες`}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-bold">{dateItem.total} υποθέσεις</Badge>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
                  </div>
                </div>

                {isExpanded && (
                  <CardContent className="p-4 pt-2 space-y-4">
                    {dateItem.types.map((typeItem) => {
                      const Icon = typeIcons[typeItem.type] || FileText;
                      const colorClass = typeColors[typeItem.type] || "bg-slate-100 text-slate-700 border-slate-200";
                      // Iter77: αν έχει φορτωθεί η πλήρης λίστα, χρησιμοποίησέ τη
                      const fullKey = `${dateItem.date}__${typeItem.type}`;
                      const rawCases = loadedFull[fullKey] || typeItem.cases || [];
                      // Για το Στάδιο 1 (ορισμός δικαστή): κρύψε υποθέσεις που έχουν ήδη ανατεθειμένο δικαστή.
                      const filteredCases = (typeItem.type === "judge_assignment")
                        ? rawCases.filter(c => !c.assigned_judge)
                        : rawCases;
                      const filteredCount = (typeItem.type === "judge_assignment") ? filteredCases.length : typeItem.count;
                      if (filteredCount === 0) return null;
                      const hasMore = typeItem.count > (typeItem.cases?.length || 0) && !loadedFull[fullKey];
                      return (
                        <div key={typeItem.type}>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge className={`${colorClass} border text-xs`}>
                              <Icon className="w-3 h-3 mr-1" />
                              {typeLabels[typeItem.type] || typeItem.title || typeItem.type}
                            </Badge>
                            <span className="text-xs text-slate-400">({filteredCount} υποθέσεις)</span>
                            {typeItem.type === "judge_assignment" && (
                              <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                                Παραμένει ορατό μέχρι την ανάθεση δικαστή
                              </span>
                            )}
                            {hasMore && (
                              <button
                                data-testid={`load-full-${dateItem.date}-${typeItem.type}`}
                                onClick={() => fetchFullByDateAndType(dateItem.date, typeItem.type)}
                                disabled={!!loadingFull[fullKey]}
                                className="text-[11px] text-blue-600 hover:text-blue-800 font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {loadingFull[fullKey]
                                  ? "Φόρτωση…"
                                  : `Φόρτωσε όλες (${typeItem.count - (typeItem.cases?.length || 0)} ακόμη)`}
                              </button>
                            )}
                            {loadedFull[fullKey] && (
                              <span className="text-[10px] text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded">
                                Πλήρης λίστα φορτώθηκε
                              </span>
                            )}
                          </div>
                          <div className="space-y-3 ml-2">
                            {groupCasesByPinakio(filteredCases).map((grp) => {
                              const grpKey = `${dateItem.date}-${typeItem.type}-${grp.key}`;
                              const shown = expandedTypes[grpKey] ? grp.cases : grp.cases.slice(0, 20);
                              return (
                                <div key={grpKey} className="border border-slate-200 rounded-md bg-white">
                                  <div className="flex items-center justify-between flex-wrap gap-2 px-2 py-1.5 bg-slate-50 border-b border-slate-200 rounded-t-md">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="bg-indigo-100 text-indigo-800 text-[11px] font-mono px-2 py-0.5 rounded">
                                        Δικάσιμος: {fmtGreek(grp.hearing)}
                                      </span>
                                      <span className="text-xs font-semibold text-slate-700">
                                        {grp.pinakio}
                                      </span>
                                    </div>
                                    <span className="text-[11px] text-slate-500">
                                      {grp.cases.length} υπόθ.
                                    </span>
                                  </div>
                                  <div className="divide-y divide-slate-100">
                                    {shown.map((c, i) => (
                                      <Link key={i} to={`/cases/${c.case_id}`}
                                        className="flex items-center justify-between p-2 hover:bg-slate-50 transition-colors group"
                                        data-testid={`deadline-item-${c.case_id}-${typeItem.type}`}>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-mono text-sm font-medium text-slate-800">
                                            ΓΑΚ {c.gak_number}/{c.gak_year}
                                          </span>
                                          <span className="font-mono text-xs text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
                                            ΕΑΚ {c.eak_number || "—"}/{c.gak_year}
                                          </span>
                                          {c.court_type && (
                                            <span className="text-xs text-slate-400">{c.court_type}</span>
                                          )}
                                          {c.deadline_start && c.deadline_date && c.deadline_start !== c.deadline_date && (
                                            <span className="text-[10px] font-mono text-slate-500">
                                              {String(c.deadline_start).split("-").reverse().join("/")} → {String(c.deadline_date).split("-").reverse().join("/")}
                                            </span>
                                          )}
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500" />
                                      </Link>
                                    ))}
                                    {grp.cases.length > 20 && !expandedTypes[grpKey] && (
                                      <button
                                        onClick={() => setExpandedTypes(prev => ({...prev, [grpKey]: true}))}
                                        className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1.5 cursor-pointer font-medium hover:underline w-full text-left"
                                      >
                                        ...και {grp.cases.length - 20} ακόμα - Εμφάνιση όλων
                                      </button>
                                    )}
                                    {grp.cases.length > 20 && expandedTypes[grpKey] && (
                                      <button
                                        onClick={() => setExpandedTypes(prev => ({...prev, [grpKey]: false}))}
                                        className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1.5 cursor-pointer w-full text-left"
                                      >
                                        Σύμπτυξη
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                )}
              </Card>
            );
          })}

          {!showAll && filteredDates.length > visibleDates.length && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowAll(true)}
              data-testid="show-all-dates-btn"
            >
              Εμφάνιση όλων ({filteredDates.length - visibleDates.length} ακόμα ημερομηνίες)
            </Button>
          )}
          {showAll && filteredDates.length > 10 && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowAll(false)}
            >
              Σύμπτυξη
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Today;
